import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ChevronLeft, ChevronDown, ChevronUp, BookOpen, Rocket, 
  Activity, Apple, Moon, Syringe, FlaskConical, Pill, Calendar,
  Mic, Camera, Brain, MessageSquare, Users, ChefHat, Clock,
  FileText, Heart, Lightbulb, Sparkles, Bell, Settings,
  CheckCircle, ArrowRight, Flame, Cloud, Baby, TrendingUp,
  BarChart3, ListChecks, LineChart, Zap
} from 'lucide-react';

const HowToUsePage = () => {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    quickStart: true,
    timeline: true,
    trackers: false,
    journaling: false,
    ai: false,
    community: false,
    tools: false,
    resources: false,
    tips: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const Section = ({ id, title, icon: Icon, children, color = "primary" }) => (
    <Card className="overflow-hidden">
      <button
        onClick={() => toggleSection(id)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        data-testid={`section-${id}`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full bg-${color}/10 flex items-center justify-center`}>
            <Icon className={`w-5 h-5 text-${color}`} />
          </div>
          <h2 className="text-lg font-semibold text-[#2D2D2D]">{title}</h2>
        </div>
        {expandedSections[id] ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {expandedSections[id] && (
        <div className="px-4 pb-4 pt-2 border-t">
          {children}
        </div>
      )}
    </Card>
  );

  const FeatureItem = ({ icon: Icon, title, description, link }) => (
    <div className="flex gap-3 py-3 border-b last:border-b-0">
      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-gray-600" />
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-[#2D2D2D]">{title}</h4>
          {link && (
            <Link to={link} className="text-primary text-sm hover:underline">
              Open →
            </Link>
          )}
        </div>
        <p className="text-sm text-gray-600 mt-1">{description}</p>
      </div>
    </div>
  );

  const Step = ({ number, title, children }) => (
    <div className="flex gap-4 py-3">
      <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 font-bold text-sm">
        {number}
      </div>
      <div>
        <h4 className="font-medium text-[#2D2D2D]">{title}</h4>
        <p className="text-sm text-gray-600 mt-1">{children}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDF8F4]">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-white/80 hover:text-white mb-4"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back to Dashboard
          </button>
          <div className="flex items-center gap-3">
            <BookOpen className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-nunito font-bold">How to Use Calm Pathways</h1>
              <p className="text-white/80 text-sm">Your complete guide to tracking and managing PANDAS/PANS</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Welcome Card */}
        <Card className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <div className="flex items-start gap-4">
            <Heart className="w-8 h-8 text-primary fill-primary flex-shrink-0" />
            <div>
              <h2 className="text-xl font-bold text-[#2D2D2D] mb-2">Welcome to Calm Pathways!</h2>
              <p className="text-gray-700">
                We're so glad you're here. This app was created by parents, for parents navigating the 
                PANDAS/PANS journey. Whether you're newly diagnosed or years into treatment, we're here 
                to help you track, understand, and advocate for your child.
              </p>
            </div>
          </div>
        </Card>

        {/* Quick Start Section */}
        <Section id="quickStart" title="Quick Start Guide" icon={Rocket}>
          <p className="text-gray-600 mb-4">
            Get up and running in just 5 minutes! Here's how to start using Calm Pathways effectively:
          </p>
          
          <div className="space-y-2">
            <Step number="1" title="Log Your First Symptom">
              Go to the <Link to="/symptoms" className="text-primary hover:underline">Symptom Tracker</Link> and 
              record how your child is doing today. Rate severity, note any symptoms, and add triggers if you know them.
            </Step>
            
            <Step number="2" title="Track What They Ate">
              Use the <Link to="/food-tracker" className="text-primary hover:underline">Food Tracker</Link> to 
              log meals. This helps identify food sensitivities that may worsen symptoms.
            </Step>
            
            <Step number="3" title="Record Sleep">
              Log sleep quality in the <Link to="/sleep" className="text-primary hover:underline">Sleep Tracker</Link>. 
              Sleep disturbances are common in PANDAS/PANS and tracking helps identify patterns.
            </Step>
            
            <Step number="4" title="Explore the Knowledge Base">
              Visit the <Link to="/knowledge" className="text-primary hover:underline">Knowledge Base</Link> to 
              learn about PANDAS/PANS, treatments, and what to expect.
            </Step>
            
            <Step number="5" title="Connect with the Community">
              Join the <Link to="/forum" className="text-primary hover:underline">Community Forum</Link> to 
              connect with other parents who understand your journey.
            </Step>
          </div>

          <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
              <CheckCircle className="w-5 h-5" />
              Pro Tip: Daily Consistency
            </div>
            <p className="text-sm text-green-800">
              The most valuable insights come from consistent daily tracking. Even on good days, 
              take 2-3 minutes to log symptoms, food, and sleep. Over time, you'll see patterns 
              that can help guide treatment decisions.
            </p>
          </div>
        </Section>

        {/* Timeline & Insights Section - THE #1 FEATURE */}
        <Section id="timeline" title="Timeline & Insights - Your Most Powerful Tool" icon={TrendingUp}>
          <div className="p-4 bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg border border-primary/20 mb-4">
            <p className="text-gray-700 font-medium">
              <Zap className="w-5 h-5 inline mr-2 text-primary" />
              This is the #1 benefit of Calm Pathways! The Timeline brings together ALL your tracking data 
              into one powerful view, helping you see patterns, correlations, and trends that would be 
              impossible to spot otherwise.
            </p>
          </div>

          <h3 className="font-semibold text-[#2D2D2D] mt-4 mb-3">How to Access the Timeline</h3>
          <div className="p-4 bg-gray-50 rounded-lg mb-4">
            <p className="text-sm text-gray-700 mb-2">
              You can access the Timeline from multiple places:
            </p>
            <ul className="text-sm text-gray-700 space-y-1 ml-4">
              <li>• Click <strong>"Timeline"</strong> button on any tracker page</li>
              <li>• Select <strong>"Timeline"</strong> from the sidebar navigation</li>
              <li>• Go directly to <Link to="/timeline" className="text-primary hover:underline">/timeline</Link></li>
            </ul>
          </div>

          <h3 className="font-semibold text-[#2D2D2D] mt-6 mb-3">Timeline Features Explained</h3>
          
          <FeatureItem
            icon={ListChecks}
            title="The Event List"
            description="A chronological list of everything you've tracked - symptoms, flares, foods, sleep, medications, appointments, and more. Filter by date range or event type to focus on what matters. Each entry is color-coded by category for quick scanning."
          />
          
          <FeatureItem
            icon={LineChart}
            title="The Graph View"
            description="Visual representation of your child's symptom severity over time. See trends at a glance - are things improving, stable, or worsening? Compare symptoms against medications, sleep quality, or other factors to spot connections."
          />
          
          <FeatureItem
            icon={BarChart3}
            title="Key Metrics"
            description="Quick summary statistics including average severity, number of flares, best and worst days, and tracking streaks. These numbers help you quantify progress for doctor visits and your own peace of mind."
          />
          
          <FeatureItem
            icon={Lightbulb}
            title="AI Insights"
            description="Our AI analyzes your data and highlights important observations like 'Symptoms tend to be worse on Mondays' or 'Sleep quality dropped 30% this week.' These automated insights surface patterns you might miss."
          />
          
          <FeatureItem
            icon={Brain}
            title="Smart Correlations"
            description="The most powerful feature - AI finds statistical correlations between different factors. Discover connections like 'Dairy consumption correlates with higher symptom severity' or 'Better sleep predicts lower anxiety scores the next day.'"
          />

          <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-center gap-2 text-amber-700 font-medium mb-2">
              <Lightbulb className="w-5 h-5" />
              Getting the Most from Timeline & Insights
            </div>
            <ul className="text-sm text-amber-800 space-y-2">
              <li><strong>Track consistently:</strong> The more data you log, the better the insights. Aim for daily entries even on good days.</li>
              <li><strong>Use all trackers:</strong> Correlations work best when you track multiple factors - symptoms, food, sleep, and medications together.</li>
              <li><strong>Check weekly:</strong> Review your Timeline at least once a week to spot emerging patterns early.</li>
              <li><strong>Share with doctors:</strong> Use the PDF export to share your Timeline data with your care team - it's incredibly valuable for appointments.</li>
            </ul>
          </div>

          <div className="mt-4 flex justify-center">
            <Button 
              onClick={() => navigate('/timeline')}
              className="bg-primary text-white hover:bg-primary/90"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Open Timeline & Insights
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </Section>

        {/* Trackers Section */}
        <Section id="trackers" title="Using the Trackers" icon={Activity}>
          <p className="text-gray-600 mb-4">
            Trackers are the heart of Calm Pathways. They help you see patterns and correlations 
            that might not be obvious day-to-day.
          </p>

          <FeatureItem
            icon={Activity}
            title="Symptom Tracker"
            description="Log daily symptoms with severity ratings (1-10), track specific symptoms like OCD, tics, anxiety, and note potential triggers. You can also track strep exposure events."
            link="/symptoms"
          />
          
          <FeatureItem
            icon={Apple}
            title="Food Tracker"
            description="Record meals and snacks throughout the day. Note any reactions to foods. Many PANDAS/PANS children have food sensitivities that can worsen symptoms."
            link="/food-tracker"
          />
          
          <FeatureItem
            icon={Moon}
            title="Sleep Tracker"
            description="Track bedtime, wake time, sleep quality, and disturbances. Poor sleep often correlates with symptom flares."
            link="/sleep"
          />
          
          <FeatureItem
            icon={Syringe}
            title="Vaccine Tracker"
            description="Record vaccinations with dates, providers, and lot numbers. Track any reactions and link them to symptom entries to identify patterns."
            link="/vaccines"
          />
          
          <FeatureItem
            icon={Cloud}
            title="Weather Tracker"
            description="Monitor weather conditions and barometric pressure changes, which some families find correlate with symptom changes."
            link="/weather"
          />
          
          <FeatureItem
            icon={Baby}
            title="Cycle Tracker"
            description="For older children, track menstrual cycles as hormonal changes can affect PANDAS/PANS symptoms."
            link="/cycle"
          />
          
          <FeatureItem
            icon={FlaskConical}
            title="Lab Tracker (Premium)"
            description="Record lab results including strep titers (ASO, Anti-DNase B), inflammatory markers, and immune panels. Track changes over time."
            link="/lab-tracker"
          />
          
          <FeatureItem
            icon={Pill}
            title="Medication & Supplement Tracker (Premium)"
            description="Track all medications and supplements with dosages, frequencies, start/stop dates, and effectiveness notes."
            link="/supplements"
          />
          
          <FeatureItem
            icon={Calendar}
            title="Appointment Tracker (Premium)"
            description="Keep track of doctor appointments, specialists visits, and therapy sessions. Add notes from each visit."
            link="/appointments"
          />
        </Section>

        {/* Journaling Section */}
        <Section id="journaling" title="Journaling Features" icon={Flame}>
          <p className="text-gray-600 mb-4">
            Sometimes you need more than checkboxes. Our journaling features let you capture 
            the full picture of what's happening.
          </p>

          <FeatureItem
            icon={Flame}
            title="Flare Journal"
            description="Document flare episodes in detail. Record what happened, potential triggers, what helped, and how long it lasted. This becomes invaluable for identifying patterns and for doctor visits."
            link="/flare-journal"
          />
          
          <FeatureItem
            icon={Mic}
            title="Voice Journal"
            description="Don't have time to type? Use voice journaling to record your observations. The app transcribes your audio automatically so you can speak naturally about your child's day."
            link="/voice-journal"
          />

          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>When to use the Flare Journal:</strong> Use it during acute flares to document 
              everything - symptoms, behaviors, what you tried, what helped. These detailed records 
              are incredibly helpful when talking to doctors or looking back to understand what worked.
            </p>
          </div>
        </Section>

        {/* AI Features Section */}
        <Section id="ai" title="AI-Powered Features" icon={Brain}>
          <p className="text-gray-600 mb-4">
            Our AI features analyze your data to help you see patterns and get answers quickly.
          </p>

          <FeatureItem
            icon={MessageSquare}
            title="AI Chat"
            description="Ask questions about PANDAS/PANS anytime. Get evidence-based information, coping strategies, and guidance. The AI is trained specifically on PANDAS/PANS research and parent experiences."
            link="/chat"
          />
          
          <FeatureItem
            icon={Brain}
            title="Smart Correlations (Premium)"
            description="Our AI analyzes your tracking data to find correlations you might miss. It looks at symptoms, food, sleep, weather, and more to identify potential patterns and triggers."
            link="/smart-correlations"
          />
          
          <FeatureItem
            icon={Sparkles}
            title="Flare Predictions (Premium)"
            description="Based on your historical data, the AI can predict when flares might be more likely, helping you prepare and potentially prevent them."
            link="/flare-predictions"
          />
          
          <FeatureItem
            icon={Camera}
            title="Symptom Photo Log (Premium)"
            description="Take photos of physical symptoms (rashes, swelling, handwriting changes) and the AI helps track changes over time and document for doctors."
            link="/symptom-photos"
          />
        </Section>

        {/* Community Section */}
        <Section id="community" title="Community Features" icon={Users}>
          <p className="text-gray-600 mb-4">
            You're not alone on this journey. Connect with other PANDAS/PANS families who understand.
          </p>

          <FeatureItem
            icon={Users}
            title="Community Forum"
            description="Ask questions, share experiences, and support each other. Topics include treatment experiences, symptom management, diet & nutrition, and emotional support."
            link="/forum"
          />
          
          <FeatureItem
            icon={ChefHat}
            title="Community Recipes"
            description="Browse and share anti-inflammatory recipes that work for PANDAS/PANS kids. Filter by dietary restrictions and see what other families recommend."
            link="/community-recipes"
          />
          
          <FeatureItem
            icon={Sparkles}
            title="What Helped"
            description="See what treatments, supplements, and strategies have helped other families. Real experiences from the community."
            link="/what-helped"
          />
          
          <FeatureItem
            icon={Heart}
            title="Success Stories"
            description="Read encouraging stories from families who have seen improvement. Hope is real."
            link="/success-stories"
          />
        </Section>

        {/* Tools Section */}
        <Section id="tools" title="Medical Tools" icon={Clock}>
          <p className="text-gray-600 mb-4">
            Powerful tools to help you track progress and communicate with your care team.
          </p>

          <FeatureItem
            icon={Clock}
            title="Medical Timeline (Premium)"
            description="See your entire medical history on one timeline - medications started/stopped, appointments, flares, vaccines, and more. Invaluable for seeing the big picture and for new doctors."
            link="/timeline"
          />
          
          <FeatureItem
            icon={FileText}
            title="Care Team Summary PDF (Premium)"
            description="Generate a professional PDF summary of your child's health data to share with doctors. Includes providers, medications, supplements, and recent appointments."
            link="/dashboard"
          />
          
          <FeatureItem
            icon={Bell}
            title="Reminders"
            description="Set reminders for medications, supplements, and appointments so nothing falls through the cracks."
            link="/reminders"
          />
        </Section>

        {/* Resources Section */}
        <Section id="resources" title="Educational Resources" icon={BookOpen}>
          <p className="text-gray-600 mb-4">
            Learn everything you need to know about PANDAS/PANS and how to advocate for your child.
          </p>

          <FeatureItem
            icon={BookOpen}
            title="Knowledge Base"
            description="Comprehensive articles about PANDAS/PANS including symptoms, diagnosis, treatments, research, and what to expect. Written for parents, reviewed by experts."
            link="/knowledge"
          />
          
          <FeatureItem
            icon={FlaskConical}
            title="Lab Education (Premium)"
            description="Understand what lab tests mean for PANDAS/PANS. Learn about strep titers, inflammatory markers, and immune panels."
            link="/lab-education"
          />
          
          <FeatureItem
            icon={Lightbulb}
            title="Coping Library"
            description="Strategies for managing difficult moments - for both your child and yourself. Includes calming techniques, sensory tools, and de-escalation tips."
            link="/coping"
          />
          
          <FeatureItem
            icon={Heart}
            title="Parent Regulation Tools"
            description="Taking care of yourself matters. Find resources for managing caregiver stress, burnout prevention, and self-care."
            link="/parent-regulation"
          />
          
          <FeatureItem
            icon={Baby}
            title="Child Regulation Tools"
            description="Activities and techniques to help your child during difficult moments - breathing exercises, sensory activities, and calming routines."
            link="/child-regulation"
          />
        </Section>

        {/* Tips Section */}
        <Section id="tips" title="Tips for Success" icon={Lightbulb}>
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="font-medium text-green-800 mb-2">📊 Track Consistently</h4>
              <p className="text-sm text-green-700">
                Even 2-3 minutes of daily tracking provides valuable data. The more consistent you are, 
                the better the AI can identify patterns and the more useful your medical timeline becomes.
              </p>
            </div>
            
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-2">📝 Use the Flare Journal</h4>
              <p className="text-sm text-blue-700">
                During a flare, write everything down. What you think triggered it, what symptoms appeared, 
                what helped, what didn't. This information is gold when talking to doctors.
              </p>
            </div>
            
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-medium text-purple-800 mb-2">👥 Connect with Community</h4>
              <p className="text-sm text-purple-700">
                Other PANDAS/PANS parents are your best resource. They've been where you are. 
                Don't hesitate to ask questions in the forum or share your experiences.
              </p>
            </div>
            
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <h4 className="font-medium text-amber-800 mb-2">🏥 Prepare for Appointments</h4>
              <p className="text-sm text-amber-700">
                Before doctor visits, review your Medical Timeline and generate a Care Team Summary PDF. 
                Having organized data makes appointments more productive.
              </p>
            </div>
            
            <div className="p-4 bg-pink-50 rounded-lg border border-pink-200">
              <h4 className="font-medium text-pink-800 mb-2">💚 Take Care of Yourself</h4>
              <p className="text-sm text-pink-700">
                Caregiver burnout is real. Use the Parent Regulation Tools and don't forget that 
                your wellbeing matters too. You can't pour from an empty cup.
              </p>
            </div>
          </div>
        </Section>

        {/* Need Help Card */}
        <Card className="p-6 bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
          <div className="text-center">
            <h3 className="text-lg font-bold text-[#2D2D2D] mb-2">Need More Help?</h3>
            <p className="text-gray-600 mb-4">
              Have questions or feedback? We're here for you.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button 
                onClick={() => navigate('/chat')}
                className="bg-primary hover:bg-primary/90"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Ask AI Chat
              </Button>
              <Button 
                onClick={() => navigate('/forum')}
                variant="outline"
                className="border-primary text-primary hover:bg-primary/10"
              >
                <Users className="w-4 h-4 mr-2" />
                Ask the Community
              </Button>
              <Button 
                onClick={() => navigate('/report-issue')}
                variant="outline"
              >
                Report an Issue
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default HowToUsePage;
